/**
 * Urban Nest Quest — Lead attribution capture
 *
 * Captures first-touch + last-touch attribution for every visitor:
 *   - UTM params (source, medium, campaign, content, term)
 *   - referrer (external only)
 *   - landing_page (first URL visited this session)
 *   - first_neighborhood (first /apartments/<slug> they viewed)
 *   - first_article (first /blog/<slug> they viewed)
 *   - last_page (most recent URL before form submit)
 *   - device_type (mobile / tablet / desktop)
 *
 * First-touch fields (landing_page, UTMs, referrer, first_neighborhood,
 * first_article) are written ONCE per session and never overwritten.
 * Last-touch fields (last_page) update on every route change.
 */

const STORAGE_KEY = "unq_attribution_v2";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

const _read = () => {
  if (typeof window === "undefined") return null;
  try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null"); } catch { return null; }
};

const _write = (data) => {
  if (typeof window === "undefined") return;
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch { /* no-op */ }
};

const _detectDevice = () => {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/iPad|tablet|Tablet/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return "mobile";
  // iPadOS reports as desktop UA — fall back to viewport check
  if (typeof window !== "undefined" && window.innerWidth <= 768) return "mobile";
  if (typeof window !== "undefined" && window.innerWidth <= 1024) return "tablet";
  return "desktop";
};

/**
 * Call once on app mount. Idempotent: only writes first-touch fields the
 * first time it's invoked per session.
 */
export const initAttribution = () => {
  if (typeof window === "undefined") return;
  const existing = _read();
  if (existing) return; // first-touch already locked in

  const params = new URLSearchParams(window.location.search);
  const data = { device_type: _detectDevice() };

  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) data[k] = v.slice(0, 120);
  });
  if (document.referrer && !document.referrer.includes(window.location.host)) {
    data.referrer = document.referrer.slice(0, 240);
  }
  data.landing_page = (window.location.pathname + window.location.search).slice(0, 240);
  data.session_started_at = new Date().toISOString();

  _write(data);
};

/**
 * Call on every route change. Records:
 *   - last_page  (always updates)
 *   - first_neighborhood / first_article (only set ONCE per session)
 */
export const trackPageView = (pathname) => {
  if (typeof window === "undefined" || !pathname) return;
  const data = _read() || { device_type: _detectDevice() };

  data.last_page = pathname.slice(0, 240);

  if (!data.first_neighborhood) {
    const m = pathname.match(/^\/apartments\/([a-z0-9-]+)/i);
    if (m) data.first_neighborhood = m[1];
  }
  if (!data.first_article) {
    const m = pathname.match(/^\/blog\/([a-z0-9-]+)/i);
    if (m) data.first_article = m[1];
  }

  _write(data);
};

/** Returns attribution object suitable for spreading into any lead form payload. */
export const getAttribution = () => {
  return _read() || { device_type: _detectDevice() };
};
